import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Root error boundary. Without one, any error thrown during render unmounts
 * the whole app and leaves a blank white page with nothing for the user to
 * act on or report. This renders a short message and a reload button instead.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[ErrorBoundary] Uncaught render error:', error, info.componentStack);
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  private handleGoHome = (): void => {
    window.location.assign('/');
  };

  render(): ReactNode {
    if (!this.state.error) return this.props.children;

    return (
      <div className="min-h-screen bg-mist flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-white rounded-xl border border-stone shadow-brand-sm p-6 flex flex-col gap-4 text-center">
          <h1 className="font-display text-h2 font-semibold text-midnight">
            Something went wrong
          </h1>
          <p className="text-sm text-slate leading-relaxed">
            The app hit an unexpected error. Reloading usually fixes it. If it keeps
            happening, let your admin know what you were doing when it happened.
          </p>
          <p className="text-[12px] font-mono text-slate break-words bg-mist rounded-lg px-3 py-2 text-left">
            {this.state.error.message}
          </p>
          <div className="flex gap-3">
            <button type="button" onClick={this.handleGoHome} className="flex-1 btn-secondary">
              Go Home
            </button>
            <button type="button" onClick={this.handleReload} className="flex-1 btn-primary">
              Reload
            </button>
          </div>
        </div>
      </div>
    );
  }
}
